import { useContext, useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Flex, Typography } from 'antd';
import classNames from 'classnames';

import { Context } from '@Pages/ProfilePage/ProfileDetails/context';
import { ReactComponent as MailIcon } from '@Icons/additional/mail.svg';
import { ReactComponent as PhoneIcon } from '@Icons/additional/phone.svg';
import { ReactComponent as SmartphoneIcon } from '@Icons/additional/smartphone.svg';
import { ReactComponent as SkyscraperIcon } from '@Icons/additional/skyscraper-4.svg';
import { ReactComponent as GpsIcon } from '@Icons/additional/gps.svg';
import { ReactComponent as ChevronRight } from '@Icons/profile/chevron-right.svg';
import { ReactComponent as AvatarPlaceholder } from '@Icons/additional/avatar-user.svg';
import { useAppSelector } from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { User } from '@Types/index';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';
import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import { useGetEmpPhotoQuery } from '@Store/api/dictionaryApiSlice';

import styles from './basic-information.module.css';
import { getWorkPhone } from './utils';

interface MainSectionRowProps {
  icon: JSX.Element;
  title: string;
  description: string | JSX.Element;
  className?: string;
}

const MainSectionRow: React.FC<MainSectionRowProps> = ({
  icon,
  title,
  description,
  className,
}) => {
  return (
    <Flex className={classNames(styles.mainSectionRow, className)}>
      <div className={styles.mainSectionRowIcon}>{icon}</div>
      <Flex vertical className={styles.mainSectionRowText}>
        <Typography.Text className={styles.mainSectionRowTitle}>
          {title}
        </Typography.Text>
        <Typography.Text ellipsis className={styles.mainSectionRowDescription}>
          {description}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};

interface MainSectionProps {
  user: User;
}

const MainSection: React.FC<MainSectionProps> = ({ user }) => {
  const workPhone = getWorkPhone(user.phone, user.extn);

  return (
    <Flex vertical>
      <Typography.Text strong className={styles.mainSectionTitle}>
        Basic Information
      </Typography.Text>
      <MainSectionRow
        icon={<MailIcon />}
        title="Email"
        description={user.email || EMPTY_FIELD_PLACEHOLDER}
      />
      <MainSectionRow
        icon={<PhoneIcon />}
        title="Work Phone"
        description={workPhone || EMPTY_FIELD_PLACEHOLDER}
      />
      <MainSectionRow
        icon={<SmartphoneIcon />}
        title="Mobile Phone"
        description={user.contact || EMPTY_FIELD_PLACEHOLDER}
      />
      <MainSectionRow
        className="company"
        icon={<SkyscraperIcon />}
        title="Company"
        description={user.company || EMPTY_FIELD_PLACEHOLDER}
      />
      <MainSectionRow
        icon={<GpsIcon />}
        title="Current Location"
        description={user.currentLocation || EMPTY_FIELD_PLACEHOLDER}
      />
    </Flex>
  );
};

interface ManagerCardProps {
  name: string;
  photo: string | null | undefined;
  occupation: string;
  onClick?: () => void;
}

const ManagerCard: React.FC<ManagerCardProps> = ({
  name,
  photo,
  occupation,
  onClick,
}) => {
  const AVATAR_SIZE = 40;

  return (
    <Flex className={styles.managerCard}>
      <Flex className={styles.managerCardContent}>
        <Avatar
          size={AVATAR_SIZE}
          icon={<AvatarPlaceholder />}
          className={styles.managerCardAvatar}
          src={photo}
        />
        <Flex vertical>
          <Typography.Text className={styles.managerCardName} strong>
            {name}
          </Typography.Text>
          <Typography className={styles.managerCardOccupation}>
            {occupation || EMPTY_FIELD_PLACEHOLDER}
          </Typography>
        </Flex>
      </Flex>
      <Button type="text" icon={<ChevronRight />} onClick={onClick} />
    </Flex>
  );
};

interface ManagerSectionProps {
  user: User | undefined;
}

const ManagerSection: React.FC<ManagerSectionProps> = ({ user }) => {
  const { data: userPhoto } = useGetEmpPhotoQuery(user?.empImageName || '');
  return (
    <Flex vertical className={styles.managerSection}>
      <Typography.Text className={styles.managerSectionTitle}>
        MANAGER
      </Typography.Text>
      <ManagerCard
        name={user?.empName || ''}
        photo={userPhoto}
        occupation={user?.occupation || ''}
      />
    </Flex>
  );
};

interface BasicInformationProps {
  height: number;
  isMobile: boolean;
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  height,
  isMobile,
}) => {
  const { tabsHeight } = useContext(Context);

  const [styleHeight, setStyleHeight] = useState<
    Record<string, string> | undefined
  >(undefined);
  const [stylePosition, setStylePosition] = useState<
    Record<string, string> | undefined
  >(undefined);

  const user = useAppSelector(currentUserSelector);

  const { data: users = [] } = useGetUsersListByNameQuery(null);

  const userManager = useMemo(() => {
    const output = users.find((item) => item.empName === user.reportingManager);
    return output;
  }, [user, users]);

  useEffect(() => {
    if (tabsHeight) {
      setStylePosition({
        position: 'sticky',
        top: `${tabsHeight}px`,
      });
    }
  }, [tabsHeight]);

  useEffect(() => {
    if (height) {
      setStyleHeight({
        maxHeight: `${height}px`,
        opacity: '1',
      });
    }
  }, [height]);

  const style = isMobile
    ? undefined
    : {
        ...styleHeight,
        ...stylePosition,
      };

  return (
    <Flex
      vertical
      className={classNames(styles.wrapper, isMobile ? 'mobile' : null)}
      style={style}
    >
      <MainSection user={user} />
      <ManagerSection user={userManager} />
    </Flex>
  );
};

export default BasicInformation;
